import type { ApiResponse } from "../../../../../services/apiService";
import {
  ClaimAppeal,
  ClaimLinkCandidate,
  ClaimLinkEarlyRemit,
  ClaimLinkMatchReason,
  ClaimLinkRole,
  ClaimResubmissionOf,
  EdiObject,
} from "../../../../../types/claims";
import { formatDisplayDate } from "../../../../helpers/dataHelper";
import {
  ClaimEditError,
  formatFeeAmount,
  parseClaimEditError,
} from "../claimAppeals";
import { LinkedRemits } from "../claimTimeline";

// Contract: app-backend REV-2629, REV-2657 (api/services/claim_links.py, views/claims.py)

export const ROLE_LABELS: Record<ClaimLinkRole, string> = {
  original: "This claim is the original",
  corrected: "This claim is the corrected one",
};

export const MATCH_REASON_LABELS: Record<ClaimLinkMatchReason, string> = {
  replacement: "Replacement claim",
  sameDateOfService: "Same date of service",
  submittedAfterAction: "Submitted after the action",
  samePayer: "Same payer",
  sameBilledAmount: "Same billed amount",
  sameCptCodes: "Same CPT codes",
};

/** "#123" */
export const claimTag = (claimNumber: string) => `#${claimNumber}`;

/** "#ORIG resubmitted as #NEW" */
export const describeLink = (original: string, corrected: string) =>
  `${claimTag(original)} resubmitted as ${claimTag(corrected)}`;

/** The claim's correct and resubmit action; one at most (REV-2628). */
export const getResubmitAction = (
  appeals: ClaimAppeal[] | undefined | null,
): ClaimAppeal | null =>
  (appeals || []).find((appeal) => appeal.actionType === "correct_resubmit") ??
  null;

/** A correct and resubmit action not linked yet: the claim can still be an original. */
export const getOpenResubmitAction = (
  appeals: ClaimAppeal[] | undefined | null,
): ClaimAppeal | null => {
  const action = getResubmitAction(appeals);
  return action && !action.link ? action : null;
};

/** Mirrors the backend's default_role: the claim with a correct and resubmit
 * action still to link is the original, else the corrected one. */
export const defaultLinkRole = (
  appeals: ClaimAppeal[] | undefined | null,
): ClaimLinkRole => (getOpenResubmitAction(appeals) ? "original" : "corrected");

/** Whether the claim can take either side of a new link: an original with an
 * open action, or a corrected claim not already some claim's resubmission. */
export const canTakeLinkRole = (
  role: ClaimLinkRole,
  appeals: ClaimAppeal[] | undefined | null,
  resubmissionOf: ClaimResubmissionOf | null | undefined,
): boolean =>
  role === "original" ? !!getOpenResubmitAction(appeals) : !resubmissionOf;

export const canLinkAnySide = (
  appeals: ClaimAppeal[] | undefined | null,
  resubmissionOf: ClaimResubmissionOf | null | undefined,
): boolean =>
  canTakeLinkRole("original", appeals, resubmissionOf) ||
  canTakeLinkRole("corrected", appeals, resubmissionOf);

/** The claim's own EDI objects, without the corrected claim's remits its
 * detail embeds on an original (REV-2657). */
export const getOwnEdiObjects = (
  ediObjects: EdiObject[] | undefined | null,
): EdiObject[] => (ediObjects || []).filter((ediObj) => !ediObj.fromClaimNumber);

/** On an original: the corrected claim's remits its fee counts, embedded in
 * its own detail and tagged fromClaimNumber (REV-2657). */
export const getLinkedRemits = (
  ediObjects: EdiObject[] | undefined | null,
): LinkedRemits | null => {
  const remits = (ediObjects || []).filter((ediObj) => !!ediObj.fromClaimNumber);
  return remits.length
    ? { fromClaimNumber: remits[0].fromClaimNumber as string, remits }
    : null;
};

/** The warning for remits of the corrected claim dated on or before the action. */
export const describeEarlyRemits = (
  earlyRemits: ClaimLinkEarlyRemit[],
  childClaimNumber: string,
  actionOn: string,
): string | null => {
  if (!earlyRemits.length) return null;
  const list = earlyRemits
    .map((remit) =>
      [
        remit.amount != null ? formatFeeAmount(remit.amount) : null,
        remit.paidOn ? `on ${formatDisplayDate(remit.paidOn)}` : null,
      ]
        .filter(Boolean)
        .join(" "),
    )
    .join(", ");
  const single = earlyRemits.length === 1;
  return (
    `${single ? "A remit" : `${earlyRemits.length} remits`} of ${claimTag(childClaimNumber)} ` +
    `${single ? "is" : "are"} dated on or before the action date (${formatDisplayDate(actionOn)}): ${list}. ` +
    "Check that this is the right claim: a payment from before the resubmission isn't from it."
  );
};

/** "Jane Doe · DOS Jun 3, 2026 · Aetna" */
export const describeCandidate = (candidate: ClaimLinkCandidate) =>
  [
    candidate.patientName,
    candidate.dateOfService &&
      `DOS ${formatDisplayDate(candidate.dateOfService)}`,
    candidate.payer,
  ]
    .filter(Boolean)
    .join(" · ");

export type ClaimLinkErrorCode =
  | "validation_error"
  | "claim_not_found"
  | "no_resubmit_action"
  | "already_linked"
  | "child_already_linked"
  | "same_claim"
  | "link_cycle"
  | "different_patient"
  | "not_found"
  | "unknown";

export type ClaimLinkField = "otherClaimNumber" | "role" | "reason";

export type ClaimLinkError = ClaimEditError<ClaimLinkErrorCode, ClaimLinkField>;

const KNOWN_CODES: ClaimLinkErrorCode[] = [
  "validation_error",
  "claim_not_found",
  "no_resubmit_action",
  "already_linked",
  "child_already_linked",
  "same_claim",
  "link_cycle",
  "different_patient",
  "not_found",
];

const FIELDS: ClaimLinkField[] = ["otherClaimNumber", "role", "reason"];

export const parseClaimLinkError = (
  response: ApiResponse<unknown>,
): ClaimLinkError => {
  const error = parseClaimEditError(response, KNOWN_CODES, FIELDS);
  // The API's message ("Claim #X is not in our system yet...") is the one to show
  const body = response.data as { message?: unknown } | null;
  return typeof body?.message === "string" && body.message
    ? { ...error, message: body.message }
    : error;
};
